import { useState, useMemo } from "react";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import { alpha, useTheme } from "@mui/material/styles";
import {
  DSTableCell,
  DSTable,
  DSTableHead,
  DSTableRow,
  DSTableBody,
  DSTableContainer,
} from "views/system-design/Table";
import DSChip from "views/system-design/DSChip";
import DSBox from "views/system-design/DSBox";
import DSTypography from "views/system-design/DSTypography";
import DSCheckbox from "views/system-design/DSCheckBox";
import DSPopover from "views/system-design/DSPopover";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
  Ticket,
  TicketStatus,
  TICKET_STATUSES,
  STATUS_COLORS,
  TYPE_COLORS,
  PRIORITY_COLORS,
} from "../../types";
import { useTickets } from "../../context/TicketsContext";
import {
  formatDate,
  getStatusLabel,
  getTypeLabel,
  getPriorityLabel,
  canChangeStatus,
  useOptimisticState,
  resolveUserName,
} from "../../utils";
import { useLocalTicketOrder } from "../../hooks/useLocalTicketOrder";
import { useIsMobile } from "../../hooks/useIsMobile";
import { Author } from "api/author";

interface TicketsTableProps {
  tickets: Ticket[];
  users: Author[];
  onSelectTicket: (ticket: Ticket) => void;
  selectedTicketId?: string;
}

type ColumnDef = {
  field: string;
  label: string;
  sortable?: boolean;
  width: string;
  mobileWidth?: string;
  hideOnMobile?: boolean;
};

const columns: ColumnDef[] = [
  { field: "drag", label: "", width: "2%", hideOnMobile: false },
  { field: "backlog", label: "Backlog", width: "3%", hideOnMobile: true },
  {
    field: "key",
    label: "Key",
    sortable: true,
    width: "7%",
    mobileWidth: "15%",
  },
  {
    field: "type",
    label: "Type",
    sortable: true,
    width: "7%",
    hideOnMobile: true,
  },
  {
    field: "priority",
    label: "Priority",
    sortable: true,
    width: "7%",
    hideOnMobile: true,
  },
  {
    field: "title",
    label: "Title",
    sortable: true,
    width: "24%",
    mobileWidth: "40%",
  },
  {
    field: "status",
    label: "Status",
    sortable: true,
    width: "10%",
    mobileWidth: "20%",
  },
  {
    field: "assignee",
    label: "Assignee",
    sortable: true,
    width: "12%",
    mobileWidth: "25%",
  },
  {
    field: "creatorId",
    label: "Creator",
    sortable: true,
    width: "10%",
    hideOnMobile: true,
  },
  {
    field: "createdAt",
    label: "Created",
    sortable: true,
    width: "10%",
    hideOnMobile: true,
  },
  {
    field: "updatedAt",
    label: "Updated",
    sortable: true,
    width: "10%",
    hideOnMobile: true,
  },
  {
    field: "dueDate",
    label: "Due Date",
    sortable: true,
    width: "10%",
    hideOnMobile: true,
  },
  { field: "labels", label: "Labels", width: "13%", hideOnMobile: true },
];

const PRIORITY_RANK: Record<string, number> = {
  low: 0,
  medium: 1,
  high: 2,
  critical: 3,
};

function sortTickets(
  tickets: Ticket[],
  sortState: Record<string, 1 | -1>,
  getEffectiveOrder: (ticket: Ticket) => number,
): Ticket[] {
  return [...tickets].sort((firstTicket, secondTicket) => {
    if (firstTicket.isBacklog !== secondTicket.isBacklog)
      return firstTicket.isBacklog ? 1 : -1;

    if (Object.keys(sortState).length > 0) {
      for (const [field, direction] of Object.entries(sortState)) {
        if (field === "priority") {
          const firstRank = firstTicket.priority
            ? (PRIORITY_RANK[firstTicket.priority] ?? -1)
            : -1;
          const secondRank = secondTicket.priority
            ? (PRIORITY_RANK[secondTicket.priority] ?? -1)
            : -1;
          if (firstRank < secondRank) return -direction;
          if (firstRank > secondRank) return direction;
          continue;
        }
        const firstValue = firstTicket[field as keyof Ticket];
        const secondValue = secondTicket[field as keyof Ticket];
        if (firstValue == null && secondValue == null) continue;
        if (firstValue == null) return direction;
        if (secondValue == null) return -direction;
        if (firstValue < secondValue) return -direction;
        if (firstValue > secondValue) return direction;
      }
    }
    return getEffectiveOrder(firstTicket) - getEffectiveOrder(secondTicket);
  });
}

export default function TicketsTable({
  tickets,
  users,
  onSelectTicket,
  selectedTicketId,
}: TicketsTableProps) {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const { updateTicket } = useTickets();
  const { getEffectiveOrder, calculateAndSetOrder } = useLocalTicketOrder();
  const [sortState, setSortState] = useState<Record<string, 1 | -1>>({});
  const [statusPopup, setStatusPopup] = useState<{
    anchorEl: HTMLElement;
    ticket: Ticket;
  } | null>(null);

  const computed = useMemo(
    () => sortTickets(tickets, sortState, getEffectiveOrder),
    [tickets, sortState, getEffectiveOrder],
  );
  const [displayTickets, setDisplayTickets] = useOptimisticState(computed);

  const handleSort = (field: string) => {
    setSortState((prev) => {
      const current = prev[field];
      if (!current) return { [field]: 1 };
      if (current === 1) return { [field]: -1 };
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const isSorted = Object.keys(sortState).length > 0;

  const handleStatusChange = (ticket: Ticket, newStatus: TicketStatus) => {
    if (!canChangeStatus(ticket, newStatus)) return;
    updateTicket(ticket.id, { status: newStatus });
    setStatusPopup(null);
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination || isSorted) return;
    const { source, destination } = result;
    if (source.index === destination.index) return;

    const reordered = [...displayTickets];
    const [moved] = reordered.splice(source.index, 1);
    reordered.splice(destination.index, 0, moved);
    setDisplayTickets(reordered);

    const newIndex = destination.index;
    const prevTicket = newIndex > 0 ? reordered[newIndex - 1] : null;
    const nextTicket =
      newIndex < reordered.length - 1 ? reordered[newIndex + 1] : null;

    calculateAndSetOrder(moved.id, prevTicket, nextTicket);
  };

  const visibleColumns = columns.filter(
    (columnDef) => !isMobile || !columnDef.hideOnMobile,
  );

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <DSTableContainer sx={{ flex: 1, overflow: "auto" }}>
        <DSTable stickyHeader>
          <DSTableHead>
            <DSTableRow>
              {visibleColumns.map((columnDef) => (
                <DSTableCell
                  key={columnDef.field}
                  sx={{
                    width:
                      isMobile && columnDef.mobileWidth
                        ? columnDef.mobileWidth
                        : columnDef.width,
                    whiteSpace: "nowrap",
                    cursor: columnDef.sortable ? "pointer" : "default",
                    userSelect: "none",
                    "&:hover": columnDef.sortable
                      ? { backgroundColor: "action.hover" }
                      : {},
                  }}
                  onClick={() =>
                    columnDef.sortable && handleSort(columnDef.field)
                  }
                >
                  <DSBox
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    {columnDef.label}
                    {columnDef.sortable && (
                      <>
                        {sortState[columnDef.field] === 1 ? (
                          <ArrowUpwardIcon fontSize="small" color="primary" />
                        ) : sortState[columnDef.field] === -1 ? (
                          <ArrowDownwardIcon fontSize="small" color="primary" />
                        ) : (
                          <SwapVertIcon
                            fontSize="small"
                            sx={{ opacity: 0.3 }}
                          />
                        )}
                      </>
                    )}
                  </DSBox>
                </DSTableCell>
              ))}
            </DSTableRow>
          </DSTableHead>
          <Droppable droppableId="tickets-table">
            {(provided) => (
              <DSTableBody ref={provided.innerRef} {...provided.droppableProps}>
                {displayTickets.map((ticket, ticketIndex) => (
                  <Draggable
                    key={ticket.id}
                    draggableId={ticket.id}
                    index={ticketIndex}
                    isDragDisabled={isSorted || ticket.isBacklog}
                  >
                    {(dragProvided, snapshot) => (
                      <DSTableRow
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        onClick={() => onSelectTicket(ticket)}
                        style={{
                          ...dragProvided.draggableProps.style,
                          cursor: "pointer",
                          backgroundColor: snapshot.isDragging
                            ? theme.palette.paper.elevation8
                            : ticket.id === selectedTicketId
                              ? alpha(theme.palette.info.light, 0.15)
                              : ticket.isBacklog
                                ? alpha(theme.palette.warning.light, 0.15)
                                : "inherit",
                          boxShadow: snapshot.isDragging
                            ? `0 4px 12px ${alpha(theme.palette.common.black, 0.15)}`
                            : "none",
                          display: snapshot.isDragging ? "table" : undefined,
                          tableLayout: snapshot.isDragging
                            ? "fixed"
                            : undefined,
                          width: snapshot.isDragging ? "100%" : undefined,
                        }}
                      >
                        <DSTableCell
                          sx={{ px: 0.5, width: "2%", color: "text.disabled" }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {!isSorted && !ticket.isBacklog && (
                            <DSBox
                              {...dragProvided.dragHandleProps}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                cursor: "grab",
                                "&:active": { cursor: "grabbing" },
                              }}
                            >
                              <DragIndicatorIcon fontSize="small" />
                            </DSBox>
                          )}
                        </DSTableCell>
                        {!isMobile && (
                          <DSTableCell sx={{ px: 0.5 }}>
                            <DSCheckbox
                              size="small"
                              checked={ticket.isBacklog}
                              onClick={(event) => event.stopPropagation()}
                              onChange={(event) => {
                                event.stopPropagation();
                                updateTicket(ticket.id, {
                                  isBacklog: !ticket.isBacklog,
                                });
                              }}
                              title="Backlog"
                            />
                          </DSTableCell>
                        )}
                        <DSTableCell
                          sx={{ fontWeight: 600, color: "text.secondary" }}
                        >
                          {ticket.key}
                        </DSTableCell>
                        {!isMobile && (
                          <DSTableCell>
                            <DSChip
                              label={getTypeLabel(ticket.type)}
                              size="small"
                              sx={{
                                bgcolor: TYPE_COLORS[ticket.type] || "grey.600",
                                color: "common.white",
                                fontWeight: 500,
                                fontSize: 11,
                              }}
                            />
                          </DSTableCell>
                        )}
                        {!isMobile && (
                          <DSTableCell>
                            {ticket.priority && (
                              <DSChip
                                label={getPriorityLabel(ticket.priority)}
                                size="small"
                                sx={{
                                  bgcolor:
                                    PRIORITY_COLORS[ticket.priority] ||
                                    "grey.500",
                                  color: "common.white",
                                  fontWeight: 500,
                                  fontSize: 11,
                                }}
                              />
                            )}
                          </DSTableCell>
                        )}
                        <DSTableCell>{ticket.title}</DSTableCell>
                        <DSTableCell>
                          <DSChip
                            label={getStatusLabel(ticket.status)}
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              setStatusPopup({
                                anchorEl: event.currentTarget,
                                ticket,
                              });
                            }}
                            sx={{
                              bgcolor: STATUS_COLORS[ticket.status],
                              color: "common.white",
                              fontWeight: 500,
                              cursor: "pointer",
                              "&:hover": { opacity: 0.85 },
                            }}
                          />
                        </DSTableCell>
                        <DSTableCell>
                          {resolveUserName(ticket.assignee, users) || "-"}
                        </DSTableCell>
                        {!isMobile && (
                          <DSTableCell>
                            {resolveUserName(ticket.creatorId, users) || "-"}
                          </DSTableCell>
                        )}
                        {!isMobile && (
                          <DSTableCell>
                            {ticket.createdAt
                              ? formatDate(ticket.createdAt)
                              : "-"}
                          </DSTableCell>
                        )}
                        {!isMobile && (
                          <DSTableCell>
                            {ticket.updatedAt
                              ? formatDate(ticket.updatedAt)
                              : "-"}
                          </DSTableCell>
                        )}
                        {!isMobile && (
                          <DSTableCell>
                            {ticket.dueDate ? formatDate(ticket.dueDate) : "-"}
                          </DSTableCell>
                        )}
                        {!isMobile && (
                          <DSTableCell>
                            <DSBox
                              sx={{
                                display: "flex",
                                gap: 0.5,
                                flexWrap: "wrap",
                              }}
                            >
                              {ticket.labels.map((label) => (
                                <DSChip
                                  key={label}
                                  label={label}
                                  size="small"
                                  variant="outlined"
                                />
                              ))}
                            </DSBox>
                          </DSTableCell>
                        )}
                      </DSTableRow>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </DSTableBody>
            )}
          </Droppable>
        </DSTable>
      </DSTableContainer>
      <DSPopover
        open={!!statusPopup}
        anchorEl={statusPopup?.anchorEl}
        onClose={() => setStatusPopup(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: { sx: { minWidth: 140, py: 0.5 } },
        }}
      >
        {statusPopup &&
          TICKET_STATUSES.filter(
            (statusDef) => statusDef.value !== statusPopup.ticket.status,
          ).map((statusDef) => (
            <DSBox
              key={statusDef.value}
              onClick={() =>
                handleStatusChange(statusPopup.ticket, statusDef.value)
              }
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.5,
                py: 0.75,
                cursor: "pointer",
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              <DSBox
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  bgcolor: STATUS_COLORS[statusDef.value],
                }}
              />
              <DSTypography variant="body2">{statusDef.label}</DSTypography>
            </DSBox>
          ))}
      </DSPopover>
    </DragDropContext>
  );
}
